import type { InlineInputProps } from '@ztechapps/webui.input'
import { Icon } from '@ztechapps/webui.util'
import { CustomerAddress } from '@ztechapps/customer.webui'
import { AddressEditField } from '@ztechapps/webui.location'
import { humanize } from '@ztechapps/common.utils'
import { enrichAddressWithCustomer } from '@ztechapps/shipping.common'
import type { SetOptional, SetRequired } from 'type-fest'
import { logisticUpdateOrderAddressQuery } from '@ztechapps/logistic.common'
import type { Address, Customer, LogisticVendorOrShipToEnum } from '@ztechapps/common.contract'

type InputProps = {
  vendorOrShipTo: Customer
  logisticId: string
  addressType: LogisticVendorOrShipToEnum
}

export type LogisticAddressInlineInputProps = SetOptional<
  SetRequired<InlineInputProps<InputProps>, 'inputProps' | 'parentInputComponent'>,
  'name'
> & {
  disabledReason?: string
}
export const LogisticAddressInlineInput = ({
  inputProps,
  name,
  label,
  initialValues,
  mutationConfig,
  canEdit,
  disabledReason,
  parentInputComponent,
}: LogisticAddressInlineInputProps) => {
  const addressType = inputProps.addressType
  // 'vendorAddress' | 'shipToAddress' — uniquely identifies which of the two logistic surfaces
  // is open, since both render through this same component.
  const editorKey = `${addressType}Address`
  name = name ?? editorKey

  const storedAddress = initialValues?.[editorKey] as Address
  // The Address snapshot is a point-in-time copy of the customer's address and its phone is never
  // resynced, while the vendor/shipTo Customer snapshot IS rewritten on every customer save
  // (CustomerRepository's postSave -> UpdateRelatedToCustomer), so the contact's phone here is the
  // current one. This feeds the READ-ONLY display only: `initialValues` still carries the stored
  // address, so the editor pre-fills and saves what is stored and this fallback is never persisted.
  // A phone on the logistic's own address wins, and a side with no stored address never reaches
  // this value — both editor arms decide their empty state from `initialValues`.
  //
  // The same fallback the carrier-bound payload resolves, through the one helper that owns it
  // (`libs/shipping/common/src/utils/enrichAddressWithCustomer.ts:16`), so the phone this block
  // shows and the phone the label is bought with can never disagree. Only `phone` is taken from
  // it: the display's `name` line is the address's own, unlike the carrier payload's.
  const displayAddress = {
    ...storedAddress,
    phone: enrichAddressWithCustomer(storedAddress, inputProps.vendorOrShipTo).phone,
  } as Address

  // `initialValues` and `canEdit` are both straight pass-throughs: `LogisticAddress.tsx` computes
  // and supplies them directly from `useLogisticItemContext()` (`documentResult`'s document for
  // the former, `canEdit` — the same role, document-status and lock check `Context.tsx:41-46`
  // derives — for the latter). No document fetch and no fallback here on purpose: an earlier
  // version called `useLogisticDocument`/`useLogisticDocumentLock` in this wrapper to re-derive
  // both. That query is a genuinely new round-trip, independent of the page's own already-loaded
  // logistic document, and resolving after the page settles is the same mechanism that reflowed
  // the order items table on the sales item page — the page beneath this block must never move.

  return (
    <AddressEditField
      editorKey={editorKey}
      name={name}
      label={label as string}
      initialValues={initialValues}
      displayAddress={displayAddress}
      mutationConfig={mutationConfig ?? { query: logisticUpdateOrderAddressQuery }}
      canEdit={canEdit}
      disabledReason={disabledReason}
      parentInputComponent={parentInputComponent}
      addressSelectComponent={CustomerAddress}
      addressSelectProps={{
        filter: { _id: inputProps.vendorOrShipTo?._id },
      }}
      updateParams={({ _id, updatedValue }) => {
        return {
          input: {
            _id,
            addressType,
            address: updatedValue,
            logisticId: inputProps.logisticId,
            customerId: inputProps.vendorOrShipTo?._id,
          },
        }
      }}
      emptyContent={
        <>
          <Icon name="map-marker" />{' '}
          <small>{`Click here to fill it up ${humanize(inputProps.addressType)} address.`}</small>
        </>
      }
    />
  )
}
